/**
 * CreditsSection: 04 Credits and guardrails on the metered Usage page: the
 * credit tiles, the credit bar, the daily credit limit with its presets, and
 * two folds (credit activity and the rate card). Reads and writes the same
 * endpoints Settings · Billing does (billingData.ts). Nothing is for sale
 * here: the plan is managed by YAGNI.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Btn, Fold } from "../queue/index.js";
import {
  creditActivity,
  formatCredits,
  useBilling,
  useGuardrails,
  type GuardrailsData,
} from "./billingData.js";
import { Bar2, Note, OfTotal, Sec, Tile, Tiles } from "./usageParts.js";

const DAILY_LIMIT_PRESETS = [5, 10, 25, 50, 100] as const;

/** The published card, per million tokens. A contract's negotiated rates replace it. */
const RATE_CARD = [
  { lane: "Efficient", input: "$0.50", cached: "$0.05", output: "$2", claude: "Haiku 4.5" },
  { lane: "Standard", input: "$1", cached: "$0.10", output: "$5", claude: "Sonnet 5.5" },
  { lane: "Advanced", input: "$2", cached: "$0.25", output: "$8", claude: "Opus 5.5" },
  { lane: "Peak", input: "$4.50", cached: "$0.50", output: "$18", claude: "Fable 5.1" },
] as const;

/** "Dec 11, 00:00": when the daily ceiling next resets (midnight UTC). */
function resetsAt(guardrails: GuardrailsData): string {
  const base = guardrails.dailyTokenResetAt ? new Date(guardrails.dailyTokenResetAt) : new Date();
  const next = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate() + 1));
  return `${next.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}, 00:00 UTC`;
}

/**
 * The daily credit limit as a field: presets, a custom amount, Save. Shared by
 * both contracts' guardrails; a credit is a dollar of metered spend.
 */
export function DailyCeilingField({
  state,
  labelWidth = 140,
  help,
}: {
  state: ReturnType<typeof useGuardrails>;
  labelWidth?: number;
  help?: string;
}) {
  const { guardrails, loaded, saving, error, save } = state;
  const ceiling = guardrails?.dailyCreditCeiling ?? null;
  const [draft, setDraft] = useState(ceiling === null ? "" : String(ceiling));
  const previous = useRef(ceiling);
  useEffect(() => {
    if (previous.current === ceiling) return;
    previous.current = ceiling;
    setDraft(ceiling === null ? "" : String(ceiling));
  }, [ceiling]);

  if (!loaded) return null;
  if (!guardrails) {
    return <Note>The daily credit limit could not be loaded. Change it under Settings · Billing.</Note>;
  }
  const parsed = Number(draft);
  const valid = draft.trim() !== "" && Number.isFinite(parsed) && parsed >= 1;
  const changed = valid && parsed !== guardrails.dailyCreditCeiling;
  const atLimit = guardrails.dailyCreditCeiling > 0 && guardrails.dailyCreditsUsed >= guardrails.dailyCreditCeiling;
  let helpText = help ?? `Resets ${resetsAt(guardrails)}. Teams pause at the ceiling and say so.`;
  if (atLimit) helpText = "Agents are paused until the ceiling is raised or tomorrow's reset.";
  if (error) helpText = error;
  if (!valid) helpText = "Enter a number of at least 1.";

  return (
    <div className="field" style={{ gridTemplateColumns: `${labelWidth}px 1fr` }} data-testid="daily-ceiling">
      <label htmlFor="usage-daily-ceiling">Daily credit limit</label>
      <div className="inline">
        <span className="seg" role="group" aria-label="Daily limit presets">
          {DAILY_LIMIT_PRESETS.map((value) => (
            <button
              key={value}
              type="button"
              className={parsed === value ? "on" : undefined}
              aria-pressed={parsed === value}
              onClick={() => setDraft(String(value))}
            >
              ${value}
            </button>
          ))}
        </span>
        <div className="in" style={{ width: 120, flex: "none" }}>
          <input
            id="usage-daily-ceiling"
            type="number"
            inputMode="decimal"
            min={1}
            step={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label="New daily ceiling"
          />
        </div>
        <Btn variant="secondary" size="sm" disabled={!changed || saving} onClick={() => void save(parsed)}>
          {saving ? "Saving" : "Save"}
        </Btn>
        <span className="fact" style={{ fontSize: 11 }}>
          {helpText}
        </span>
      </div>
    </div>
  );
}

export function CreditsSection() {
  const billing = useBilling();
  const guardrails = useGuardrails();
  const { overview, credits } = billing;
  const subscribed = overview?.currentPeriodStart != null;
  const g = guardrails.guardrails;

  const balance = overview?.credits ?? null;
  const consumed = balance ? Math.max(0, balance.total - balance.remaining) : 0;
  const leftShare = balance && balance.total > 0 ? (100 * balance.remaining) / balance.total : 0;

  return (
    <Sec
      k="04"
      title="Credits and guardrails"
      testId="usage-credits"
      right={
        overview ? (
          <>
            <Btn variant="quiet" size="sm" disabled={billing.busy} onClick={() => void billing.manageBilling()}>
              Invoices and receipts
            </Btn>
            <Link className="btn secondary sm" to="/settings/spending">
              Billing
            </Link>
          </>
        ) : undefined
      }
    >
      {billing.loaded && !overview && (
        <Note>Credits could not be loaded. Figures above are unaffected.</Note>
      )}

      {overview && balance && (
        <>
          <Note>Your plan is managed by YAGNI. To change it or add credits, talk to us.</Note>
          <Tiles testId="credit-tiles">
            <Tile
              value={formatCredits(balance.remaining)}
              label="Credits left"
              tone={leftShare <= 5 ? "amber" : "green"}
              sub={[
                `of ${formatCredits(balance.total)}`,
                subscribed ? `plan ${formatCredits(balance.included)}` : `starter ${formatCredits(balance.starterGranted)}`,
                balance.granted > 0 ? `gifted ${formatCredits(balance.granted)}` : null,
                balance.purchasedPacks > 0 ? `purchased ${formatCredits(balance.purchasedPacks)}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
            {g && (
              <Tile
                value={<OfTotal n={formatCredits(g.dailyCreditsUsed)} of={formatCredits(g.dailyCreditCeiling)} />}
                label="Daily credit limit"
                tone="blue"
                sub="resets 00:00 UTC · Teams pause at the ceiling"
              />
            )}
            {overview.status === "past_due" && (
              <Tile value="Past due" label="Payment" tone="amber" sub="open invoices to settle it" />
            )}
          </Tiles>
          <Bar2
            label={`${formatCredits(balance.remaining)} of ${formatCredits(balance.total)} credits left, ${formatCredits(consumed)} used`}
            parts={[{ share: leftShare, className: leftShare <= 5 ? undefined : "k-ok", color: leftShare <= 5 ? "var(--honesty)" : undefined }]}
            testId="credit-bar"
          />
        </>
      )}

      <DailyCeilingField state={guardrails} />

      {billing.actionError && (
        <p role="alert" className="fact" style={{ color: "var(--honesty)" }}>
          {billing.actionError}
        </p>
      )}

      {credits && (
        <Fold title="Credit activity" count="by day">
          <CreditActivity billing={billing} />
        </Fold>
      )}

      <Fold title="The rate card" count="per million tokens · in · cached · out">
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Lane</th>
                <th className="num">Input</th>
                <th className="num">Cached input</th>
                <th className="num">Output</th>
                <th>Compared against</th>
              </tr>
            </thead>
            <tbody>
              {RATE_CARD.map((rate) => (
                <tr key={rate.lane}>
                  <td>{rate.lane}</td>
                  <td className="num">{rate.input}</td>
                  <td className="num">{rate.cached}</td>
                  <td className="num">{rate.output}</td>
                  <td className="fact">{rate.claude} at list · cache writes 1.25×</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Note>
          The published card. A contract can carry its own rates and lock them for 12 months; the ledger above is
          always priced at the rates this workspace is billed. Balanced is a routing policy that resolves to
          Advanced; it is never a price.
        </Note>
      </Fold>

    </Sec>
  );
}

function CreditActivity({ billing }: { billing: ReturnType<typeof useBilling> }) {
  const data = billing.credits!;
  const lines = creditActivity(data);
  if (lines.length === 0) {
    return <Note>No credit activity yet. Your Agents' work will appear here.</Note>;
  }
  const remaining = data.total - data.transactions.length;
  return (
    <div className="trail" data-testid="credit-activity">
      {lines.map((line) => (
        <div key={line.key}>
          <span className="t">{line.day}</span>
          <span className="v" style={{ width: 64, color: line.amount > 0 ? "var(--accent-active)" : "var(--text-tertiary)" }}>
            {line.amount > 0 ? "+" : "−"}
            {formatCredits(Math.abs(line.amount))}
          </span>
          <span>{line.label}</span>
        </div>
      ))}
      {remaining > 0 && (
        <div>
          <span className="t" />
          <span className="v" style={{ width: 64 }} />
          <button
            type="button"
            className="fact"
            style={{ cursor: "pointer", color: "var(--accent)" }}
            disabled={billing.moreLoading}
            onClick={() => void billing.loadMoreActivity()}
          >
            {billing.moreLoading ? "Loading" : `Show more (${remaining} remaining)`}
          </button>
        </div>
      )}
      {billing.moreError && (
        <div role="alert" style={{ color: "var(--honesty)" }}>
          {billing.moreError}
        </div>
      )}
    </div>
  );
}
